import React, { useEffect, useRef, useState } from "react";
import {
  Button, DatePicker, Input, Select, Switch,
} from "antd";
import { toast } from "react-toastify";
import { fetchTestCategory } from "../../../actions/chemistry";
import { getSearchOptions } from "../../../actions/batchReports";
import { getUserList } from "../../../actions/sendReports";
import { getLabOpsReasons } from "../../../actions/labOpsAnalytics";
import { buildTestOptionsFromCategories } from "./utils/overdueUtils";
import { RCA_STATUS_FILTER_OPTIONS } from "./utils/rcaUtils";
import {
  filterCatalog,
  filterField,
  getDefaultLastOneMonthDateRange,
  loadDataOpsAssigneeSelectOptions,
} from "./utils/utils";

const { RangePicker } = DatePicker;

const hasCatalog = (catalogs, catalogKey) => catalogs.includes(catalogKey);

const filterOptionByLabel = (inputValue, option) => (
  String(option?.label ?? "").toLowerCase().includes(String(inputValue ?? "").toLowerCase())
);

const buildCreatedByOptions = (users = []) => (
  users
    .slice()
    .sort((leftUser, rightUser) => {
      const leftName = `${leftUser.first_name} ${leftUser.last_name}`.trim().toLowerCase();
      const rightName = `${rightUser.first_name} ${rightUser.last_name}`.trim().toLowerCase();
      return leftName.localeCompare(rightName);
    })
    .map((user) => ({
      value: user.id,
      label: `${user.first_name} ${user.last_name}`.trim(),
    }))
);

/**
 * Shared Data Ops filter bar. Parent owns filter values; this component loads option catalogs.
 * Tabs pass fields/catalogs config from utils.
 */
export default function DataOpsFilters({
  idPrefix,
  fields,
  catalogs = [],
  filters,
  onFiltersChange,
  onReasonOptionsChange,
  onCategoryOptionsChange,
  onCustomerOptionsChange,
  onExport,
  exportLoading = false,
  exportDisabled = false,
}) {
  const isMounted = useRef(true);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [categoriesCatalog, setCategoriesCatalog] = useState([]);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [createdByOptions, setCreatedByOptions] = useState([]);
  const [assignToOptions, setAssignToOptions] = useState([]);
  const [overdueReasonOptions, setOverdueReasonOptions] = useState([]);
  const [retestReasonOptions, setRetestReasonOptions] = useState([]);
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [searchInput, setSearchInput] = useState(filters.search || "");

  const testOptions = buildTestOptionsFromCategories(
    categoriesCatalog,
    filters.testCategoryId,
  );

  const loadCategories = hasCatalog(catalogs, filterCatalog.categories);
  const loadCustomers = hasCatalog(catalogs, filterCatalog.customers);
  const loadUsers = hasCatalog(catalogs, filterCatalog.users);
  const loadDataOpsAssignees = hasCatalog(catalogs, filterCatalog.dataOpsAssignees);
  const loadOverdueReasons = hasCatalog(catalogs, filterCatalog.overdueReasons);
  const loadRetestReasons = hasCatalog(catalogs, filterCatalog.retestReasons);

  useEffect(() => {
    isMounted.current = true;

    const loadFilterOptions = async () => {
      setOptionsLoading(true);

      const pendingRequests = [];
      if (loadCategories) {
        pendingRequests.push(["categories", fetchTestCategory()]);
      }
      if (loadCustomers) {
        pendingRequests.push(["customers", getSearchOptions()]);
      }
      if (loadUsers) {
        pendingRequests.push(["users", getUserList()]);
      }
      if (loadDataOpsAssignees) {
        pendingRequests.push(["dataOpsAssignees", loadDataOpsAssigneeSelectOptions()]);
      }
      if (loadOverdueReasons) {
        pendingRequests.push(["overdueReasons", getLabOpsReasons(["standard", "labops"])]);
      }
      if (loadRetestReasons) {
        pendingRequests.push(["retestReasons", getLabOpsReasons(["retest"])]);
      }

      const responses = await Promise.all(pendingRequests.map(([, request]) => request));
      if (!isMounted.current) {
        return;
      }

      const responseByCatalog = {};
      pendingRequests.forEach(([catalogKey], index) => {
        responseByCatalog[catalogKey] = responses[index];
      });

      if (responseByCatalog.categories) {
        const categoryResponse = responseByCatalog.categories;
        if (categoryResponse.success) {
          const categories = Array.isArray(categoryResponse.results) ? categoryResponse.results : [];
          const nextCategoryOptions = categories.map((category) => ({
            value: category.id,
            label: category.test_category_name,
          }));
          setCategoryOptions(nextCategoryOptions);
          setCategoriesCatalog(categories);
          if (onCategoryOptionsChange) {
            onCategoryOptionsChange(nextCategoryOptions);
          }
        } else {
          toast.error(categoryResponse.message || "Failed to load test categories.");
        }
      }

      if (responseByCatalog.customers) {
        const customerResponse = responseByCatalog.customers;
        if (customerResponse.success) {
          const customers = customerResponse.list || [];
          const nextCustomerOptions = customers
            .filter((customer) => customer?.id != null)
            .map((customer) => ({
              // Lab Ops APIs expect customer.id (integer PK), not business customer_id
              value: customer.id,
              label: `${customer.company} (${customer.customer_id})`,
              customerId: customer.customer_id != null ? String(customer.customer_id) : "",
              companyName: customer.company != null ? String(customer.company) : "",
            }));
          setCustomerOptions(nextCustomerOptions);
          if (onCustomerOptionsChange) {
            onCustomerOptionsChange(nextCustomerOptions);
          }
        } else {
          toast.error(customerResponse.message || "Failed to load customers.");
        }
      }

      if (responseByCatalog.users) {
        const userResponse = responseByCatalog.users;
        if (userResponse.success) {
          setCreatedByOptions(buildCreatedByOptions(userResponse.results || []));
        } else {
          toast.error(userResponse.message || "Failed to load users.");
        }
      }

      if (responseByCatalog.dataOpsAssignees) {
        const assigneeResponse = responseByCatalog.dataOpsAssignees;
        if (assigneeResponse.success) {
          setAssignToOptions(assigneeResponse.options || []);
        } else {
          toast.error(assigneeResponse.message || "Failed to load Assign To users.");
        }
      }

      if (responseByCatalog.overdueReasons) {
        const reasonResponse = responseByCatalog.overdueReasons;
        if (reasonResponse.success) {
          const reasons = reasonResponse.data || reasonResponse.results || [];
          const nextReasonOptions = reasons
            .filter((reason) => (
              reason?.id != null
              && reason?.name != null
              && String(reason.name).trim() !== ""
            ))
            .map((reason) => ({
              value: reason.id,
              label: reason.name,
            }));
          setOverdueReasonOptions(nextReasonOptions);
          if (onReasonOptionsChange) {
            onReasonOptionsChange(nextReasonOptions);
          }
        } else if (!reasonResponse.canceled) {
          toast.error(reasonResponse.message || "Failed to load overdue reasons.");
        }
      }

      if (responseByCatalog.retestReasons) {
        const reasonResponse = responseByCatalog.retestReasons;
        if (reasonResponse.success) {
          const reasons = reasonResponse.data || reasonResponse.results || [];
          setRetestReasonOptions(
            reasons
              .filter((reason) => reason?.name != null && String(reason.name).trim() !== "")
              .map((reason) => ({
                value: reason.name,
                label: reason.name,
              })),
          );
        } else if (!reasonResponse.canceled) {
          toast.error(reasonResponse.message || "Failed to load retest reasons.");
        }
      }

      setOptionsLoading(false);
    };

    loadFilterOptions();

    return () => {
      isMounted.current = false;
    };
  }, [
    loadCategories,
    loadCustomers,
    loadUsers,
    loadDataOpsAssignees,
    loadOverdueReasons,
    loadRetestReasons,
    onReasonOptionsChange,
    onCategoryOptionsChange,
    onCustomerOptionsChange,
  ]);

  const updateFilters = (partial) => {
    onFiltersChange({ ...filters, ...partial });
  };

  const handleDateRangeChange = (nextRange) => {
    updateFilters({ dateRange: nextRange || getDefaultLastOneMonthDateRange() });
  };

  const applySearch = (value) => {
    const trimmedValue = String(value ?? "").trim();
    setSearchInput(trimmedValue);
    updateFilters({ search: trimmedValue });
  };

  const fieldId = (suffix) => `data-ops-${idPrefix}-${suffix}`;

  const renderField = (fieldKey) => {
    switch (fieldKey) {
      case filterField.dateRange:
        return (
          <div key={fieldKey} className="DataOpsAnalytics__FilterField">
            <label htmlFor={fieldId("date-range")}>
              Workorder Date Range
            </label>
            <RangePicker
              id={fieldId("date-range")}
              className="DataOpsAnalytics__DateRange"
              format="MM/DD/YYYY"
              value={filters.dateRange}
              onChange={handleDateRangeChange}
              allowClear={false}
            />
          </div>
        );

      case filterField.status:
        return (
          <div key={fieldKey} className="DataOpsAnalytics__FilterField">
            <label htmlFor={fieldId("status")}>Status</label>
            <Select
              id={fieldId("status")}
              className="DataOpsAnalytics__Select"
              popupClassName="DataOpsAnalytics__SelectMenu"
              allowClear
              options={RCA_STATUS_FILTER_OPTIONS}
              value={filters.status === "" ? "" : filters.status}
              onChange={(value) => updateFilters({ status: value ?? "" })}
              onClear={() => updateFilters({ status: "" })}
              listHeight={400}
            />
          </div>
        );

      case filterField.assignTo:
        return (
          <div key={fieldKey} className="DataOpsAnalytics__FilterField">
            <label htmlFor={fieldId("assign-to")}>Assign To</label>
            <Select
              id={fieldId("assign-to")}
              className="DataOpsAnalytics__Select"
              popupClassName="DataOpsAnalytics__SelectMenu"
              allowClear
              showSearch
              loading={optionsLoading}
              options={[{ value: "", label: "All" }, ...assignToOptions]}
              value={filters.assignTo === "" ? "" : filters.assignTo}
              onChange={(value) => updateFilters({ assignTo: value ?? "" })}
              filterOption={filterOptionByLabel}
              listHeight={400}
            />
          </div>
        );

      case filterField.testCategory:
        return (
          <div key={fieldKey} className="DataOpsAnalytics__FilterField">
            <label htmlFor={fieldId("category")}>Test category</label>
            <Select
              id={fieldId("category")}
              className="DataOpsAnalytics__Select"
              popupClassName="DataOpsAnalytics__SelectMenu"
              mode="multiple"
              allowClear
              showSearch
              maxTagCount="responsive"
              placeholder="All Categories"
              loading={optionsLoading}
              options={categoryOptions}
              value={filters.testCategoryId}
              onChange={(value) => {
                const nextCategoryIds = value || [];
                const nextTestOptions = buildTestOptionsFromCategories(
                  categoriesCatalog,
                  nextCategoryIds,
                );
                const allowedTestIds = new Set(nextTestOptions.map((option) => option.value));
                const nextTestIds = filters.testId.filter((testId) => (
                  allowedTestIds.has(testId)
                ));
                updateFilters({
                  testCategoryId: nextCategoryIds,
                  testId: nextTestIds,
                });
              }}
              filterOption={filterOptionByLabel}
              listHeight={400}
            />
          </div>
        );

      case filterField.test:
        return (
          <div key={fieldKey} className="DataOpsAnalytics__FilterField">
            <label htmlFor={fieldId("test")}>Test</label>
            <Select
              id={fieldId("test")}
              className="DataOpsAnalytics__Select"
              popupClassName="DataOpsAnalytics__SelectMenu"
              mode="multiple"
              allowClear
              showSearch
              maxTagCount="responsive"
              placeholder="All Tests"
              loading={optionsLoading}
              options={testOptions}
              value={filters.testId}
              onChange={(value) => updateFilters({ testId: value || [] })}
              filterOption={filterOptionByLabel}
              listHeight={400}
            />
          </div>
        );

      case filterField.customer:
        return (
          <div key={fieldKey} className="DataOpsAnalytics__FilterField">
            <label htmlFor={fieldId("customer")}>Customer</label>
            <Select
              id={fieldId("customer")}
              className="DataOpsAnalytics__Select"
              popupClassName="DataOpsAnalytics__SelectMenu"
              allowClear
              showSearch
              placeholder="All Customers"
              loading={optionsLoading}
              options={customerOptions}
              value={filters.customerId === "" ? undefined : filters.customerId}
              onChange={(value) => updateFilters({ customerId: value ?? "" })}
              filterOption={filterOptionByLabel}
              listHeight={400}
            />
          </div>
        );

      case filterField.search:
        return (
          <div key={fieldKey} className="DataOpsAnalytics__FilterField">
            <label htmlFor={fieldId("search")}>Search</label>
            <Input.Search
              id={fieldId("search")}
              className="DataOpsAnalytics__Search"
              allowClear
              placeholder="Search by WO, GBID"
              value={searchInput}
              onChange={(event) => {
                const nextValue = event.target.value;
                setSearchInput(nextValue);
                if (nextValue === "") {
                  updateFilters({ search: "" });
                }
              }}
              onSearch={applySearch}
            />
          </div>
        );

      case filterField.createdBy:
        return (
          <div key={fieldKey} className="DataOpsAnalytics__FilterField">
            <label htmlFor={fieldId("created-by")}>Created by</label>
            <Select
              id={fieldId("created-by")}
              className="DataOpsAnalytics__Select"
              popupClassName="DataOpsAnalytics__SelectMenu"
              allowClear
              showSearch
              placeholder="All Creators"
              loading={optionsLoading}
              options={createdByOptions}
              value={filters.createdBy === "" ? undefined : filters.createdBy}
              onChange={(value) => updateFilters({ createdBy: value ?? "" })}
              filterOption={filterOptionByLabel}
              listHeight={400}
            />
          </div>
        );

      case filterField.overdueReason:
        return (
          <div key={fieldKey} className="DataOpsAnalytics__FilterField">
            <label htmlFor={fieldId("reason")}>Overdue reason</label>
            <Select
              id={fieldId("reason")}
              className="DataOpsAnalytics__Select"
              popupClassName="DataOpsAnalytics__SelectMenu"
              allowClear
              showSearch
              placeholder="All Reasons"
              loading={optionsLoading}
              options={overdueReasonOptions}
              value={filters.reasonId === "" ? undefined : filters.reasonId}
              onChange={(value) => updateFilters({ reasonId: value ?? "" })}
              filterOption={filterOptionByLabel}
              listHeight={400}
            />
          </div>
        );

      case filterField.retestReason:
        return (
          <div key={fieldKey} className="DataOpsAnalytics__FilterField">
            <label htmlFor={fieldId("retest-reason")}>Retest reason</label>
            <Select
              id={fieldId("retest-reason")}
              className="DataOpsAnalytics__Select"
              popupClassName="DataOpsAnalytics__SelectMenu"
              allowClear
              showSearch
              placeholder="All Retest Reasons"
              loading={optionsLoading}
              options={retestReasonOptions}
              value={filters.retestReason === "" ? undefined : filters.retestReason}
              onChange={(value) => updateFilters({ retestReason: value ?? "" })}
              filterOption={filterOptionByLabel}
              listHeight={400}
            />
          </div>
        );

      case filterField.retest:
        return (
          <div key={fieldKey} className="DataOpsAnalytics__FilterField DataOpsAnalytics__FilterToggle">
            <label htmlFor={fieldId("retest")}>Retest</label>
            <Switch
              id={fieldId("retest")}
              checked={filters.retest}
              onChange={(checked) => updateFilters({ retest: checked })}
            />
          </div>
        );

      case filterField.subscriptionOnly:
        return (
          <div key={fieldKey} className="DataOpsAnalytics__FilterField DataOpsAnalytics__FilterToggle">
            <label htmlFor={fieldId("subscription")}>Subscription</label>
            <Switch
              id={fieldId("subscription")}
              checked={filters.subscriptionOnly}
              onChange={(checked) => updateFilters({ subscriptionOnly: checked })}
            />
          </div>
        );

      case filterField.export:
        return (
          <div key={fieldKey} className="DataOpsAnalytics__FilterField DataOpsAnalytics__FilterExport">
            <Button
              type="primary"
              className="DataOpsAnalytics__ExportButton"
              onClick={onExport}
              loading={exportLoading}
              disabled={exportDisabled || exportLoading}
            >
              Export Excel
            </Button>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="DataOpsAnalytics__FilterBar">
      {fields.map(renderField)}
    </div>
  );
}
